import { useEffect, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Camera, ChevronLeft, Images, Save, Sparkles, X,
  User, Heart, MapPin, GraduationCap, Briefcase, Users, Leaf, ShieldCheck,
} from 'lucide-react'
import toast from 'react-hot-toast'
import axios from 'axios'
import { profileApi } from '../../api/profile'
import { masterApi } from '../../api/master'
import type { ProfileDTO, EducationLevel, Occupation, MatrimonialStatus } from '../../types'

// Identity fields (name, DOB, gender) are locked after creation — corrections go via support.
const SUPPORT_EMAIL = 'ahirwalmatrimony@gmail.com'

const OCCUPATION_OPTS = [
  { v: 'GOVT_JOB', l: 'Government Job' },
  { v: 'ARMY', l: 'Army / Defence' },
  { v: 'PRIVATE_JOB', l: 'Private Job / Corporate' },
  { v: 'BUSINESS', l: 'Business' },
  { v: 'SELF_EMPLOYED', l: 'Self-employed / Professional (Doctor / Lawyer / CA etc.)' },
  { v: 'FARMER', l: 'Farmer / Agriculture' },
  { v: 'STUDENT', l: 'Student' },
  { v: 'HOMEMAKER', l: 'Homemaker' },
  { v: 'OTHER', l: 'Other' },
]

export default function EditProfilePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isNewUser = searchParams.get('new') === '1'
  const qc = useQueryClient()

  const avatarInputRef = useRef<HTMLInputElement>(null)
  const galleryInputRef = useRef<HTMLInputElement>(null)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [uploadingGallery, setUploadingGallery] = useState(false)
  const [govtIdType, setGovtIdType] = useState('AADHAAR')
  const [govtIdBusy, setGovtIdBusy] = useState(false)
  const biodataInputRef = useRef<HTMLInputElement>(null)
  const [biodataBusy, setBiodataBusy] = useState(false)
  const [biodataFilled, setBiodataFilled] = useState(false)

  const handleGovtIdUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) { toast.error('Please select an image of your ID'); return }
    if (file.size > 8 * 1024 * 1024) { toast.error('Image must be under 8 MB'); return }
    setGovtIdBusy(true)
    try {
      const { data } = await profileApi.govtIdUploadUrl()
      await axios.put(data.data.uploadUrl, file, { headers: { 'Content-Type': file.type }, withCredentials: false })
      await profileApi.submitGovtId(govtIdType)
      toast.success('ID submitted — we’ll review it shortly')
      qc.invalidateQueries({ queryKey: ['profile'] })
      qc.invalidateQueries({ queryKey: ['myProfile'] })
    } catch (err: any) {
      toast.error(`Upload failed: ${err?.response?.data?.message ?? err?.message ?? 'try again'}`)
    } finally {
      setGovtIdBusy(false)
      e.target.value = ''
    }
  }

  // Biodata auto-fill (create mode): downscale → presigned PUT → Claude reads it →
  // merge into the form. Only ever PRE-FILLS — the user reviews before saving.
  const downscaleJpeg = (file: File, maxDim = 2000): Promise<Blob> =>
    new Promise((resolve, reject) => {
      const img = new Image()
      const url = URL.createObjectURL(file)
      img.onload = () => {
        URL.revokeObjectURL(url)
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height))
        const canvas = document.createElement('canvas')
        canvas.width = Math.round(img.width * scale)
        canvas.height = Math.round(img.height * scale)
        canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height)
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode failed'))), 'image/jpeg', 0.88)
      }
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('not an image')) }
      img.src = url
    })

  const handleBiodataUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) { toast.error('Please select an image of your biodata'); return }
    setBiodataBusy(true)
    try {
      const jpeg = await downscaleJpeg(file)
      const { data } = await profileApi.biodataUploadUrl()
      await axios.put(data.data.uploadUrl, jpeg, { headers: { 'Content-Type': 'image/jpeg' }, withCredentials: false })
      const res = await profileApi.extractBiodata()
      const { gotraName, motherGotraName, ...fields } = res.data.data as Record<string, any>
      setForm((prev) => {
        const merged: Record<string, any> = { ...prev }
        for (const [k, v] of Object.entries(fields)) if (v !== null && v !== undefined) merged[k] = v
        return merged
      })
      const byName = (name: string) => gotras?.find((g) => g.name.toLowerCase() === name.toLowerCase())
      if (gotraName) {
        const match = byName(gotraName)
        setIsCustomGotra(!match)
        setForm((p) => ({ ...p, gotraId: match?.id ?? null, gotraCustom: match ? null : gotraName }))
      }
      if (motherGotraName) {
        const match = byName(motherGotraName)
        setIsCustomMotherGotra(!match)
        setForm((p) => ({ ...p, motherGotraId: match?.id ?? null, motherGotraCustom: match ? null : motherGotraName }))
      }
      setBiodataFilled(true)
      toast.success('Filled from your biodata — please review everything before saving')
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Couldn't read that image — please fill the form manually.")
    } finally {
      setBiodataBusy(false)
    }
  }

  const handleAvatarSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5 MB'); return }
    setUploadingAvatar(true)
    try {
      const { data: presignData } = await profileApi.avatarPresignedUrl(file.type)
      const { uploadUrl, downloadUrl } = presignData.data
      await axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type }, withCredentials: false })
      set('avatarUrl', downloadUrl)
      toast.success('Photo uploaded! Save profile to confirm.')
    } catch (err: any) {
      toast.error(`Photo upload failed: ${err?.response?.data?.message ?? err?.message ?? 'Unknown error'}`)
    } finally {
      setUploadingAvatar(false)
      if (avatarInputRef.current) avatarInputRef.current.value = ''
    }
  }

  const handleRemoveAvatar = async () => {
    if (!form.avatarUrl || uploadingAvatar) return
    setUploadingAvatar(true)
    try {
      await profileApi.removeAvatar()
      set('avatarUrl', null)
      toast.success('Photo removed')
    } catch {
      toast.error('Could not remove photo')
    } finally {
      setUploadingAvatar(false)
    }
  }

  const handleGallerySelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5 MB'); return }
    if (!existingProfile) {
      toast.error('Please save your profile first before adding gallery photos')
      if (galleryInputRef.current) galleryInputRef.current.value = ''
      return
    }
    if ((form.galleryUrls ?? []).length >= 5) {
      toast.error('Maximum 5 gallery photos allowed')
      return
    }
    setUploadingGallery(true)
    try {
      const { data: presignData } = await profileApi.galleryPresignedUrl(file.type)
      const { uploadUrl, downloadUrl } = presignData.data
      await axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type }, withCredentials: false })
      const { data: updatedProfile } = await profileApi.addGalleryPhoto(downloadUrl)
      if (!updatedProfile?.data) throw new Error('Please save your profile first before adding gallery photos')
      qc.setQueryData(['profile', 'me'], updatedProfile.data)
      set('galleryUrls', updatedProfile.data.galleryUrls ?? [])
      toast.success('Gallery photo added!')
    } catch (err: any) {
      toast.error(`Gallery upload failed: ${err?.response?.data?.message ?? err?.message ?? 'Unknown error'}`)
    } finally {
      setUploadingGallery(false)
      if (galleryInputRef.current) galleryInputRef.current.value = ''
    }
  }

  const handleRemoveGalleryPhoto = async (index: number) => {
    try {
      const { data: updatedProfile } = await profileApi.removeGalleryPhoto(index)
      qc.setQueryData(['profile', 'me'], updatedProfile.data)
      set('galleryUrls', updatedProfile.data.galleryUrls)
      toast.success('Photo removed')
    } catch {
      toast.error('Failed to remove photo')
    }
  }

  const { data: existingProfile } = useQuery({
    queryKey: ['profile', 'me'],
    queryFn: () => profileApi.getMyProfile().then((r) => r.data.data).catch(() => null),
  })

  const { data: gotras } = useQuery({
    queryKey: ['gotras'],
    queryFn: () => masterApi.getGotras().then((r) => r.data.data),
  })

  const [form, setForm] = useState<Partial<ProfileDTO>>({})
  const [isCustomGotra, setIsCustomGotra] = useState(false)
  const [isCustomMotherGotra, setIsCustomMotherGotra] = useState(false)
  const [isCustomGrandmotherGotra, setIsCustomGrandmotherGotra] = useState(false)
  const [dealbreakers, setDealbreakers] = useState<Record<string, boolean>>({})
  const [siblings, setSiblings] = useState<{ name: string; profession: string; maritalStatus: string }[]>([])

  // Create mode for a member whose profile was lost in the server move: open pre-filled.
  const [restoredNote, setRestoredNote] = useState(false)
  useEffect(() => {
    if (existingProfile !== null) return
    profileApi.prefill().then((r) => {
      const known = r.status === 200 ? r.data?.data : null
      if (known?.fullName) {
        setForm((p) => ({ ...p, fullName: known.fullName, gender: (known.gender as ProfileDTO['gender']) ?? p.gender }))
        setRestoredNote(true)
      }
    }).catch(() => { /* not a returning member */ })
  }, [existingProfile])

  useEffect(() => {
    if (existingProfile) {
      setForm(existingProfile)
      setIsCustomGotra(!existingProfile.gotraId && !!existingProfile.gotraCustom)
      setIsCustomMotherGotra(!existingProfile.motherGotraId && !!existingProfile.motherGotraCustom)
      setIsCustomGrandmotherGotra(!existingProfile.grandmotherGotraId && !!existingProfile.grandmotherGotraCustom)
      try { setDealbreakers(JSON.parse(existingProfile.dealbreakers || '{}')) } catch { setDealbreakers({}) }
      try { setSiblings(JSON.parse(existingProfile.siblings || '[]')) } catch { setSiblings([]) }
    }
  }, [existingProfile])

  const toggleDb = (key: string) => setDealbreakers((d) => ({ ...d, [key]: !d[key] }))
  const addSibling = () => setSiblings((s) => [...s, { name: '', profession: '', maritalStatus: 'NEVER_MARRIED' }])
  const updateSibling = (i: number, k: 'name' | 'profession' | 'maritalStatus', v: string) =>
    setSiblings((s) => s.map((sib, idx) => (idx === i ? { ...sib, [k]: v } : sib)))
  const removeSibling = (i: number) => setSiblings((s) => s.filter((_, idx) => idx !== i))

  const set = <K extends keyof ProfileDTO>(k: K, v: ProfileDTO[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const buildPayload = () => ({
    ...form,
    gotraId: isCustomGotra ? null : (form.gotraId ?? null),
    gotraCustom: isCustomGotra ? (form.gotraCustom ?? null) : null,
    motherGotraId: isCustomMotherGotra ? null : (form.motherGotraId ?? null),
    motherGotraCustom: isCustomMotherGotra ? (form.motherGotraCustom ?? null) : null,
    grandmotherGotraId: isCustomGrandmotherGotra ? null : (form.grandmotherGotraId ?? null),
    grandmotherGotraCustom: isCustomGrandmotherGotra ? (form.grandmotherGotraCustom ?? null) : null,
    prefMotherGotraId: form.prefMotherGotraId ?? null,
    dealbreakers: JSON.stringify(dealbreakers),
    siblings: JSON.stringify(siblings.filter((s) => s.name.trim())),
  })

  const saveMut = useMutation({
    mutationFn: () => {
      const payload = buildPayload()
      return existingProfile
        ? profileApi.updateProfile(payload).then((r) => r.data.data)
        : profileApi.createProfile(payload).then((r) => r.data.data)
    },
    onSuccess: (savedProfile) => {
      toast.success('Profile saved!')
      qc.setQueryData(['profile', 'me'], savedProfile)
      qc.invalidateQueries({ queryKey: ['profile'] })
      navigate(isNewUser ? '/dashboard' : '/profile')
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? 'Failed to save profile')
    },
  })

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      {isNewUser && (
        <div className="text-champagne rounded-2xl p-5 mb-6 flex items-start gap-3 shadow-btn ring-1 ring-inset ring-gold-500/40" style={{ background: 'linear-gradient(135deg, #7A1B2E, #4E0F1F)' }}>
          <Sparkles size={20} className="flex-shrink-0 mt-0.5 text-gold-500" />
          <div>
            <p className="font-display font-semibold text-lg text-champagne-bright">Welcome to Ahirwal Matrimony 🙏</p>
            <p className="text-champagne/70 text-sm mt-0.5">Complete your profile so potential matches can find you. The more you fill, the better your matches.</p>
          </div>
        </div>
      )}

      {!existingProfile && (
        <div className="mb-6">
          <input ref={biodataInputRef} type="file" accept="image/*" className="hidden"
                 onChange={handleBiodataUpload} data-testid="biodata-input" />
          <button
            type="button"
            onClick={() => biodataInputRef.current?.click()}
            disabled={biodataBusy}
            data-testid="biodata-upload"
            className="w-full rounded-2xl border border-maroon-700/50 text-maroon-700 bg-ivory-25 px-5 py-4 text-left font-medium hover:bg-ivory-50 transition-colors disabled:opacity-60"
          >
            {biodataBusy ? 'Reading your biodata… (a few seconds)' : '📄  Have a biodata? Upload a photo of it — we’ll fill this form for you'}
          </button>
          {biodataFilled && (
            <p className="text-sm text-gold-600 mt-2">
              Filled from your biodata — please check everything before saving.
            </p>
          )}
        </div>
      )}

      <div className="flex items-center gap-3 mb-6">
        {!isNewUser && (
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-full bg-ivory-25 border border-ivory-200 shadow-soft flex items-center justify-center text-ink-400 hover:text-maroon-700 transition-colors">
            <ChevronLeft size={20} />
          </button>
        )}
        <div>
          <h1 className="font-display font-semibold text-2xl text-ink-900">
            {existingProfile ? 'Edit Profile' : 'Create Profile'}
          </h1>
          {restoredNote && (
            <p className="mt-2 text-sm text-gold-600">
              Welcome back! Your earlier profile was lost when we moved servers — we're sorry. Your name is filled in; please complete the rest.
            </p>
          )}
          <p className="text-sm text-ink-400 mt-0.5">Fill in your details to attract the right match</p>
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); saveMut.mutate() }} className="space-y-4">

        {/* Profile Photo */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-icon">
              <Camera size={15} />
            </div>
            <div>
              <h2 className="font-display font-semibold text-ink-900 text-base">Profile Photo</h2>
              <p className="text-xs text-ink-400">Your main photo shown to matches</p>
            </div>
          </div>
          <div className="section-body">
            <div className="flex items-center gap-5">
              <div className="relative flex-shrink-0">
                <div className="w-24 h-28 rounded-2xl bg-ivory-100 overflow-hidden border border-gold-500/50 shadow-card">
                  {form.avatarUrl
                    ? <img src={form.avatarUrl} alt="avatar" className="w-full h-full object-cover object-[50%_22%]" />
                    : <div className="w-full h-full flex items-center justify-center font-display text-4xl text-maroon-700/30">
                        {form.fullName?.charAt(0)?.toUpperCase() ?? '?'}
                      </div>
                  }
                </div>
                <button
                  type="button"
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  className="absolute -bottom-1.5 -right-1.5 w-7 h-7 bg-maroon-700 rounded-full flex items-center justify-center text-champagne shadow-sm ring-1 ring-gold-500/50 hover:bg-maroon-800 transition-colors"
                >
                  <Camera size={13} />
                </button>
              </div>
              <div>
                <input ref={avatarInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleAvatarSelect} />
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    className="btn-secondary text-sm py-2 px-4"
                  >
                    {uploadingAvatar ? 'Uploading…' : form.avatarUrl ? 'Change Photo' : 'Upload Photo'}
                  </button>
                  {form.avatarUrl && (
                    <button
                      type="button"
                      onClick={handleRemoveAvatar}
                      disabled={uploadingAvatar}
                      className="text-sm py-2 px-4 rounded-full border border-danger/30 text-danger hover:bg-danger/5 transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <p className="text-xs text-ink-400 mt-2">JPG, PNG or WebP · Max 5 MB</p>
              </div>
            </div>
          </div>
        </div>

        {/* Gallery Photos */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-icon">
              <Images size={15} />
            </div>
            <div>
              <h2 className="font-display font-semibold text-ink-900 text-base">Photo Gallery</h2>
              <p className="text-xs text-ink-400">Add up to 5 photos to boost visibility</p>
            </div>
          </div>
          <div className="section-body">
            <div className="grid grid-cols-3 gap-3">
              {(form.galleryUrls ?? []).map((url, idx) => (
                <div key={idx} className="relative aspect-[3/4] rounded-2xl overflow-hidden border border-ivory-200 shadow-soft group">
                  <img src={url} alt={`gallery ${idx + 1}`} className="w-full h-full object-cover object-[50%_22%]" />
                  <button
                    type="button"
                    onClick={() => handleRemoveGalleryPhoto(idx)}
                    className="absolute top-1.5 right-1.5 bg-black/60 text-white rounded-lg p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              {(form.galleryUrls ?? []).length < 5 && (
                <button
                  type="button"
                  onClick={() => galleryInputRef.current?.click()}
                  disabled={uploadingGallery}
                  className="aspect-[3/4] rounded-2xl border border-dashed border-ivory-300 flex flex-col items-center justify-center gap-1.5 text-ink-400 hover:border-gold-500 hover:text-gold-600 transition-colors"
                >
                  {uploadingGallery
                    ? <span className="text-xs">Uploading…</span>
                    : <><Images size={20} /><span className="text-xs font-medium">Add Photo</span></>
                  }
                </button>
              )}
            </div>
            <input ref={galleryInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleGallerySelect} />
          </div>
        </div>

        {/* Basic Info */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-icon">
              <User size={15} />
            </div>
            <h2 className="font-display font-semibold text-ink-900 text-base">Basic Information</h2>
          </div>
          <div className="section-body">
            <Field label="Full Name">
              <input className="input disabled:opacity-60 disabled:cursor-not-allowed" value={form.fullName ?? ''} disabled />
              <p className="text-xs text-ink-400 mt-1.5">Locked after profile creation — email {SUPPORT_EMAIL} to correct</p>
            </Field>
            <Field label="Full Name (Hindi)" optional>
              <input className="input" value={form.fullNameHindi ?? ''} onChange={(e) => set('fullNameHindi', e.target.value)} placeholder="हिंदी में नाम" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Gender">
                <select className="input disabled:opacity-60 disabled:cursor-not-allowed" value={form.gender ?? ''} disabled>
                  <option value="">Select</option>
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                </select>
              </Field>
              <Field label="Marital Status">
                <select className="input" value={form.maritalStatus ?? 'NEVER_MARRIED'} onChange={(e) => set('maritalStatus', e.target.value as MatrimonialStatus)}>
                  <option value="NEVER_MARRIED">Never Married</option>
                  <option value="DIVORCED">Divorced</option>
                  <option value="WIDOWED">Widowed</option>
                  <option value="SEPARATED">Separated</option>
                </select>
              </Field>
            </div>
            <Field label="Date of Birth">
              <input
                type="date"
                className="input disabled:opacity-60 disabled:cursor-not-allowed"
                value={form.dateOfBirth ?? ''}
                disabled
              />
              <p className="text-xs text-ink-400 mt-1.5">Locked after profile creation — email {SUPPORT_EMAIL} to correct</p>
            </Field>
          </div>
        </div>

        {/* Gotra & Community */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-icon">
              <Heart size={15} />
            </div>
            <h2 className="font-display font-semibold text-ink-900 text-base">Gotra & Community</h2>
          </div>
          <div className="section-body">
            <Field label="Your Gotra">
              <GotraPicker
                label="Your Gotra"
                gotras={gotras}
                selectedId={isCustomGotra ? '__custom__' : (form.gotraId ?? '')}
                customValue={form.gotraCustom ?? ''}
                isCustom={isCustomGotra}
                onSelect={(val) => {
                  if (val === '__custom__') { setIsCustomGotra(true); set('gotraId', null as any) }
                  else { setIsCustomGotra(false); set('gotraCustom', null as any); set('gotraId', val || null as any) }
                }}
                onCustomChange={(val) => set('gotraCustom', val || null as any)}
              />
            </Field>
            <Field label="Mother's Gotra">
              <GotraPicker
                label="Mother's Gotra"
                gotras={gotras}
                selectedId={isCustomMotherGotra ? '__custom__' : (form.motherGotraId ?? '')}
                customValue={form.motherGotraCustom ?? ''}
                isCustom={isCustomMotherGotra}
                onSelect={(val) => {
                  if (val === '__custom__') { setIsCustomMotherGotra(true); set('motherGotraId', null as any) }
                  else { setIsCustomMotherGotra(false); set('motherGotraCustom', null as any); set('motherGotraId', val || null as any) }
                }}
                onCustomChange={(val) => set('motherGotraCustom', val || null as any)}
              />
            </Field>
            <Field label="Grandmother's Gotra">
              <GotraPicker
                label="Grandmother's Gotra"
                gotras={gotras}
                selectedId={isCustomGrandmotherGotra ? '__custom__' : (form.grandmotherGotraId ?? '')}
                customValue={form.grandmotherGotraCustom ?? ''}
                isCustom={isCustomGrandmotherGotra}
                onSelect={(val) => {
                  if (val === '__custom__') { setIsCustomGrandmotherGotra(true); set('grandmotherGotraId', null as any) }
                  else { setIsCustomGrandmotherGotra(false); set('grandmotherGotraCustom', null as any); set('grandmotherGotraId', val || null as any) }
                }}
                onCustomChange={(val) => set('grandmotherGotraCustom', val || null as any)}
              />
            </Field>
            <Field label="Kuldevi" optional>
              <input className="input" value={form.kuldevi ?? ''} onChange={(e) => set('kuldevi', e.target.value)} placeholder="e.g., Shila Mata, Bala Sundari" />
            </Field>
          </div>
        </div>

        {/* Siblings */}
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-maroon-700" />
            <h2 className="font-display font-semibold text-ink-900 text-base">Siblings</h2>
          </div>
          <p className="text-xs text-ink-400 mb-4">Add your brothers and sisters (optional).</p>
          <div className="space-y-2">
            {siblings.map((sib, i) => (
              <div key={i} className="flex gap-2 items-center">
                <input className="input flex-1" placeholder="Name" value={sib.name}
                  onChange={(e) => updateSibling(i, 'name', e.target.value)} />
                <input className="input flex-1" placeholder="Profession" value={sib.profession}
                  onChange={(e) => updateSibling(i, 'profession', e.target.value)} />
                <select className="input w-28" value={sib.maritalStatus}
                  onChange={(e) => updateSibling(i, 'maritalStatus', e.target.value)}>
                  <option value="NEVER_MARRIED">Unmarried</option>
                  <option value="MARRIED">Married</option>
                </select>
                <button type="button" onClick={() => removeSibling(i)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg shrink-0" aria-label="Remove sibling">
                  <X size={16} />
                </button>
              </div>
            ))}
            <button type="button" onClick={addSibling}
              className="text-sm font-semibold text-maroon-700 hover:underline mt-1">
              + Add sibling
            </button>
          </div>
        </div>

        {/* Location */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-icon">
              <MapPin size={15} />
            </div>
            <h2 className="font-display font-semibold text-ink-900 text-base">Location</h2>
          </div>
          <div className="section-body">
            <Field label="Native Village" optional>
              <input className="input" value={form.nativeVillage ?? ''} onChange={(e) => set('nativeVillage', e.target.value || null as any)} placeholder="e.g., Dhanaunda, Bawal, Nangal Mundi" />
            </Field>
            <Field label="Current City">
              <input className="input" value={form.currentCity ?? ''} onChange={(e) => set('currentCity', e.target.value)} placeholder="e.g., Rewari, Gurugram, Delhi" />
            </Field>
            <Field label="Full Address" optional>
              <textarea className="input resize-none" rows={3} value={form.address ?? ''} onChange={(e) => set('address', e.target.value)} placeholder="House no., street, village, tehsil…" />
            </Field>
          </div>
        </div>

        {/* Education & Career */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-icon">
              <GraduationCap size={15} />
            </div>
            <h2 className="font-display font-semibold text-ink-900 text-base">Education & Career</h2>
          </div>
          <div className="section-body">
            <Field label="Education Level">
              <select className="input" value={form.educationLevel ?? ''} onChange={(e) => set('educationLevel', e.target.value as EducationLevel)}>
                <option value="">Select</option>
                {[
                  { v: 'BELOW_TENTH', l: 'Below 10th' },
                  { v: 'TENTH', l: '10th Pass' },
                  { v: 'TWELFTH', l: '12th Pass' },
                  { v: 'DIPLOMA', l: 'Diploma / ITI / Polytechnic' },
                  { v: 'GRADUATE', l: 'Graduate (B.A / B.Sc / B.Com / B.Tech etc.)' },
                  { v: 'POST_GRADUATE', l: 'Post Graduate (M.A / M.Sc / MBA etc.)' },
                  { v: 'DOCTORATE', l: 'Doctorate (PhD)' },
                ].map(({ v, l }) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="College / School Name" optional>
              <input className="input" value={form.educationDetail ?? ''} onChange={(e) => set('educationDetail', e.target.value)} placeholder="e.g., IIT Delhi, St. Mary's School" />
            </Field>
            <Field label="Occupation">
              <select className="input" value={form.occupation ?? ''} onChange={(e) => set('occupation', e.target.value as Occupation)}>
                <option value="">Select</option>
                {OCCUPATION_OPTS.map(({ v, l }) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Job Title / Role" optional>
                <input className="input" value={form.occupationDetail ?? ''} onChange={(e) => set('occupationDetail', e.target.value)} placeholder="e.g., Software Engineer" />
              </Field>
              <Field label="Company / Org" optional>
                <input className="input" value={form.companyName ?? ''} onChange={(e) => set('companyName', e.target.value)} placeholder="e.g., TCS, CRPF" />
              </Field>
            </div>
            <Field label="Annual Income (LPA)" optional>
              <input type="number" className="input" value={form.annualIncomeLpa ?? ''} onChange={(e) => set('annualIncomeLpa', parseFloat(e.target.value) as any)} placeholder="e.g., 5.5" />
            </Field>
          </div>
        </div>

        {/* Physical Details */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-icon">
              <Leaf size={15} />
            </div>
            <h2 className="font-display font-semibold text-ink-900 text-base">Physical Details</h2>
          </div>
          <div className="section-body">
            <Field label="Height">
              {(() => {
                const totalIn = form.heightCm ? Math.round(form.heightCm / 2.54) : null
                const dispFt  = totalIn != null ? Math.floor(totalIn / 12) : ''
                const dispIn  = totalIn != null ? totalIn % 12 : ''
                return (
                  <div className="flex gap-2">
                    <select
                      className="input"
                      value={dispFt}
                      onChange={(e) => {
                        const ft = parseInt(e.target.value)
                        if (isNaN(ft)) { set('heightCm', null as any); return }
                        const inVal = typeof dispIn === 'number' ? dispIn : 0
                        set('heightCm', Math.round((ft * 12 + inVal) * 2.54) as any)
                      }}
                    >
                      <option value="">Feet</option>
                      {[4,5,6,7].map(f => <option key={f} value={f}>{f} ft</option>)}
                    </select>
                    <select
                      className="input"
                      value={dispIn}
                      onChange={(e) => {
                        const inch = parseInt(e.target.value)
                        if (isNaN(inch)) return
                        const ftVal = typeof dispFt === 'number' ? dispFt : 5
                        set('heightCm', Math.round((ftVal * 12 + inch) * 2.54) as any)
                      }}
                    >
                      <option value="">Inches</option>
                      {[0,1,2,3,4,5,6,7,8,9,10,11].map(i => (
                        <option key={i} value={i}>{i} in</option>
                      ))}
                    </select>
                  </div>
                )
              })()}
              {form.heightCm ? <p className="text-xs text-ink-400 mt-1.5">{form.heightCm} cm</p> : null}
            </Field>
            <Field label="Weight (kg)" optional>
              <input type="number" className="input" value={form.weightKg ?? ''} onChange={(e) => set('weightKg', parseInt(e.target.value) as any)} placeholder="e.g., 65" />
            </Field>
            <Field label="Diet">
              <select className="input" value={form.diet ?? ''} onChange={(e) => set('diet', e.target.value)}>
                <option value="">Select</option>
                <option value="VEGETARIAN">Vegetarian</option>
                <option value="EGGETARIAN">Eggetarian</option>
                <option value="NON_VEGETARIAN">Non-Vegetarian</option>
              </select>
            </Field>
          </div>
        </div>

        {/* Family */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-icon">
              <Users size={15} />
            </div>
            <h2 className="font-display font-semibold text-ink-900 text-base">Family</h2>
          </div>
          <div className="section-body">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Father's Name" optional>
                <input className="input" value={form.fatherName ?? ''} onChange={(e) => set('fatherName', e.target.value)} placeholder="Father's full name" />
              </Field>
              <Field label="Father's Occupation" optional>
                <input className="input" value={form.fatherOccupation ?? ''} onChange={(e) => set('fatherOccupation', e.target.value)} placeholder="e.g., Farmer, Govt. Job" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Mother's Name" optional>
                <input className="input" value={form.motherName ?? ''} onChange={(e) => set('motherName', e.target.value)} placeholder="Mother's full name" />
              </Field>
              <Field label="Siblings Count" optional>
                <input type="number" className="input" value={form.siblingsCount ?? ''} onChange={(e) => set('siblingsCount', parseInt(e.target.value) as any)} placeholder="e.g., 2" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Family Type">
                <select className="input" value={form.familyType ?? 'JOINT'} onChange={(e) => set('familyType', e.target.value)}>
                  <option value="JOINT">Joint Family</option>
                  <option value="NUCLEAR">Nuclear Family</option>
                </select>
              </Field>
              <Field label="Land (Acres)" optional>
                <input type="number" className="input" value={form.landAcres ?? ''} onChange={(e) => set('landAcres', parseFloat(e.target.value) as any)} placeholder="e.g., 5" />
              </Field>
            </div>
          </div>
        </div>

        {/* Privacy Settings */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-icon">
              <User size={15} />
            </div>
            <h2 className="font-display font-semibold text-ink-900 text-base">Privacy</h2>
          </div>
          <div className="section-body">
            <Field label="Photo Visibility">
              <select className="input" value={(form as any).photoPrivacy ?? 'PUBLIC'} onChange={(e) => set('photoPrivacy' as any, e.target.value)}>
                <option value="PUBLIC">Public — everyone can see my photos</option>
                <option value="INTEREST_ONLY">Hidden — only visible after interest accepted</option>
              </select>
            </Field>
            <label className="flex items-start gap-3 mt-4 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 accent-maroon-700"
                checked={!!(form as any).showPhonePublicly}
                onChange={(e) => set('showPhonePublicly' as any, e.target.checked)}
              />
              <span>
                <span className="block text-sm font-medium text-ink-900">Show my phone number on my profile</span>
                <span className="block text-xs text-ink-400">Off by default. If on, anyone viewing your profile can see your number and contact you directly — no match required.</span>
              </span>
            </label>
          </div>
        </div>

        {/* ID Verification */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-icon">
              <ShieldCheck size={15} />
            </div>
            <h2 className="font-display font-semibold text-ink-900 text-base">ID Verification</h2>
          </div>
          {existingProfile?.govtIdVerified ? (
            <p className="text-sm text-maroon-700 font-medium flex items-center gap-1.5">
              <ShieldCheck size={16} /> Your ID is verified — the “ID Verified” badge shows on your profile.
            </p>
          ) : existingProfile?.govtIdStatus === 'PENDING' ? (
            <p className="text-sm text-ink-600">Your ID is under review. We’ll add the badge once it’s approved (usually within a day).</p>
          ) : (
            <>
              <p className="text-xs text-ink-400 mb-3">
                Upload a government ID to earn an “ID Verified” badge, which builds trust with families.
                <b> Only our review team sees it, it’s never shown to other members, and it’s deleted right after review.</b>
                {existingProfile?.govtIdStatus === 'REJECTED' && ' Your last submission wasn’t accepted — please upload a clear, full photo.'}
              </p>
              <div className="flex items-center gap-3">
                <select className="input flex-1" value={govtIdType} onChange={(e) => setGovtIdType(e.target.value)}>
                  <option value="AADHAAR">Aadhaar</option>
                  <option value="PAN">PAN Card</option>
                  <option value="DL">Driving License</option>
                  <option value="VOTER_ID">Voter ID</option>
                  <option value="PASSPORT">Passport</option>
                </select>
                <label className="btn-secondary text-sm whitespace-nowrap cursor-pointer">
                  {govtIdBusy ? 'Uploading…' : 'Upload ID'}
                  <input type="file" accept="image/*" className="hidden" onChange={handleGovtIdUpload} disabled={govtIdBusy} />
                </label>
              </div>
            </>
          )}
        </div>

        {/* Horoscope / Kundali */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-icon">
              <Sparkles size={15} />
            </div>
            <h2 className="font-display font-semibold text-ink-900 text-base">Horoscope / Kundali</h2>
          </div>
          <p className="text-xs text-ink-400 -mt-1 mb-4">Optional — helps families who match kundalis before proceeding.</p>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Manglik">
              <select className="input" value={form.manglik ?? 'NO'} onChange={(e) => set('manglik', e.target.value as any)}>
                <option value="NO">No</option>
                <option value="YES">Yes</option>
                <option value="PARTIAL">Anshik (Partial)</option>
              </select>
            </Field>
            <Field label="Rashi (Moon Sign)" optional>
              <input className="input" value={form.rashi ?? ''} onChange={(e) => set('rashi', e.target.value)} placeholder="e.g., Vrishabha" />
            </Field>
            <Field label="Nakshatra" optional>
              <input className="input" value={form.nakshatra ?? ''} onChange={(e) => set('nakshatra', e.target.value)} placeholder="e.g., Rohini" />
            </Field>
            <Field label="Time of Birth" optional>
              <input type="time" className="input" value={form.birthTime ?? ''} onChange={(e) => set('birthTime', e.target.value)} />
            </Field>
            <Field label="Place of Birth" optional>
              <input className="input" value={form.birthPlace ?? ''} onChange={(e) => set('birthPlace', e.target.value)} placeholder="City / town of birth" />
            </Field>
          </div>
        </div>

        {/* About */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-icon">
              <Briefcase size={15} />
            </div>
            <h2 className="font-display font-semibold text-ink-900 text-base">About & Expectations</h2>
          </div>
          <div className="section-body">
            <Field label="About Me">
              <textarea className="input resize-none" rows={4} value={form.aboutMe ?? ''} onChange={(e) => set('aboutMe', e.target.value)} placeholder="Tell something about yourself — your personality, hobbies, values…" />
            </Field>
            <Field label="Partner Expectations">
              <textarea className="input resize-none" rows={4} value={form.partnerExpectations ?? ''} onChange={(e) => set('partnerExpectations', e.target.value)} placeholder="Describe qualities you're looking for in a partner…" />
            </Field>
          </div>
        </div>

        {/* Partner Preferences + dealbreakers */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-icon">
              <Heart size={15} />
            </div>
            <h2 className="font-display font-semibold text-ink-900 text-base">Partner Preferences</h2>
          </div>
          <div className="section-body space-y-4">
            <p className="text-xs text-ink-400 -mt-1">
              These refine your matches. Turn on <span className="font-semibold text-maroon-700">Dealbreaker</span> to
              hide profiles that don't match — otherwise we just rank better matches higher.
            </p>

            {/* Age range */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="label mb-0">Age range</label>
                <DbToggle k="age" on={!!dealbreakers.age} onToggle={toggleDb} />
              </div>
              <div className="flex items-center gap-2">
                <input type="number" className="input" placeholder="Min" value={form.prefMinAge ?? ''}
                  onChange={(e) => set('prefMinAge', (e.target.value ? Number(e.target.value) : null) as any)} />
                <span className="text-ink-400 text-sm">to</span>
                <input type="number" className="input" placeholder="Max" value={form.prefMaxAge ?? ''}
                  onChange={(e) => set('prefMaxAge', (e.target.value ? Number(e.target.value) : null) as any)} />
              </div>
            </div>

            {/* Height range */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="label mb-0">Height range (cm)</label>
                <DbToggle k="height" on={!!dealbreakers.height} onToggle={toggleDb} />
              </div>
              <div className="flex items-center gap-2">
                <input type="number" className="input" placeholder="Min" value={form.prefMinHeightCm ?? ''}
                  onChange={(e) => set('prefMinHeightCm', (e.target.value ? Number(e.target.value) : null) as any)} />
                <span className="text-ink-400 text-sm">to</span>
                <input type="number" className="input" placeholder="Max" value={form.prefMaxHeightCm ?? ''}
                  onChange={(e) => set('prefMaxHeightCm', (e.target.value ? Number(e.target.value) : null) as any)} />
              </div>
            </div>

            {/* Minimum income */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="label mb-0">Minimum income (LPA)</label>
                <DbToggle k="income" on={!!dealbreakers.income} onToggle={toggleDb} />
              </div>
              <input type="number" className="input" placeholder="e.g., 8" value={form.prefMinIncomeLpa ?? ''}
                onChange={(e) => set('prefMinIncomeLpa', (e.target.value ? Number(e.target.value) : null) as any)} />
            </div>

            {/* Minimum education */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="label mb-0">Minimum education</label>
                <DbToggle k="education" on={!!dealbreakers.education} onToggle={toggleDb} />
              </div>
              <select className="input" value={form.prefEducation ?? ''}
                onChange={(e) => set('prefEducation', (e.target.value || null) as any)}>
                <option value="">Any</option>
                <option value="TWELFTH">12th</option>
                <option value="DIPLOMA">Diploma</option>
                <option value="GRADUATE">Graduate</option>
                <option value="POST_GRADUATE">Post Graduate</option>
                <option value="DOCTORATE">Doctorate</option>
              </select>
            </div>

            {/* Preferred diet */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="label mb-0">Preferred diet</label>
                <DbToggle k="diet" on={!!dealbreakers.diet} onToggle={toggleDb} />
              </div>
              <select className="input" value={form.prefDiet ?? ''}
                onChange={(e) => set('prefDiet', (e.target.value || null) as any)}>
                <option value="">Any</option>
                <option value="VEGETARIAN">Vegetarian</option>
                <option value="EGGETARIAN">Eggetarian</option>
                <option value="NON_VEGETARIAN">Non-Vegetarian</option>
              </select>
            </div>

            {/* Preferred occupations (multi-select) */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="label mb-0">Preferred occupations</label>
                <DbToggle k="occupation" on={!!dealbreakers.occupation} onToggle={toggleDb} />
              </div>
              <div className="flex flex-wrap gap-2">
                {OCCUPATION_OPTS.map(({ v, l }) => {
                  const selected = (form.prefOccupation ?? []).includes(v)
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => {
                        const cur = form.prefOccupation ?? []
                        set('prefOccupation', selected ? cur.filter((x) => x !== v) : [...cur, v])
                      }}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                        selected
                          ? 'bg-maroon-700 text-champagne border-gold-500/40'
                          : 'bg-ivory-100 text-ink-400 border-ivory-300 hover:border-maroon-700'
                      }`}
                    >
                      {l}
                    </button>
                  )
                })}
              </div>
              <p className="text-[11px] text-ink-400 mt-1.5">Leave all unselected to accept any occupation.</p>
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={saveMut.isPending}
          className="btn-primary w-full flex items-center justify-center gap-2 py-3.5"
        >
          <Save size={16} />
          {saveMut.isPending ? 'Saving…' : 'Save Profile'}
        </button>
      </form>
    </div>
  )
}

function DbToggle({ k, on, onToggle }: { k: string; on: boolean; onToggle: (k: string) => void }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(k)}
      className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full transition-colors ${
        on ? 'bg-maroon-700 text-champagne ring-1 ring-inset ring-gold-500/40' : 'bg-ivory-100 text-ink-400 hover:bg-ivory-200'
      }`}
      title="Dealbreaker: hide profiles that don't match this"
    >
      <span className={`w-1.5 h-1.5 rounded-full ${on ? 'bg-gold-500' : 'bg-ivory-300'}`} />
      Dealbreaker
    </button>
  )
}

function Field({ label, children, optional }: { label: string; children: React.ReactNode; optional?: boolean }) {
  return (
    <div>
      <label className="block text-[10px] font-bold text-gold-600 uppercase tracking-[0.28em] mb-2">
        {label}
        {optional && <span className="normal-case font-normal text-ink-400/70 ml-1 tracking-normal">(optional)</span>}
      </label>
      {children}
    </div>
  )
}

interface GotraPickerProps {
  label: string
  gotras: { id: string; name: string; nameHindi: string | null }[] | undefined
  selectedId: string
  customValue: string
  isCustom: boolean
  onSelect: (val: string) => void
  onCustomChange: (val: string) => void
}

function GotraPicker({ gotras, selectedId, customValue, isCustom, onSelect, onCustomChange }: GotraPickerProps) {
  const selectedName = gotras?.find((g) => g.id === selectedId)?.name ?? ''
  const [query, setQuery] = useState(isCustom ? customValue : selectedName)
  const [open, setOpen] = useState(false)

  // Re-sync when the profile loads / the field changes externally.
  useEffect(() => { setQuery(isCustom ? customValue : selectedName) }, [selectedId, isCustom]) // eslint-disable-line

  const q = query.trim().toLowerCase()
  const matches = (gotras ?? []).filter((g) => g.name.toLowerCase().includes(q)).slice(0, 8)
  const exact = matches.some((g) => g.name.toLowerCase() === q)

  return (
    <div className="relative">
      <input
        className="input"
        value={query}
        placeholder="Type your gotra…"
        onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && q && (
        <div className="absolute z-20 mt-1 w-full bg-ivory-25 border border-ivory-300 rounded-lg shadow-lg max-h-56 overflow-auto">
          {matches.map((g) => (
            <button
              type="button"
              key={g.id}
              onMouseDown={() => { onSelect(g.id); setQuery(g.name); setOpen(false) }}
              className="block w-full text-left px-3 py-2 hover:bg-ivory-100 text-sm text-ink-900"
            >
              {g.name}{g.nameHindi ? ` · ${g.nameHindi}` : ''}
            </button>
          ))}
          {!exact && (
            <button
              type="button"
              onMouseDown={() => { onSelect('__custom__'); onCustomChange(query.trim()); setOpen(false) }}
              className="block w-full text-left px-3 py-2 hover:bg-ivory-100 text-sm text-maroon-700 font-medium border-t border-ivory-200"
            >
              Use “{query.trim()}” (not in list)
            </button>
          )}
        </div>
      )}
    </div>
  )
}
